<template>
    <v-row justify="center">
        <v-dialog v-model="isOpen" @click:outside="closeWhenNoChanges()" @keydown.esc="closeWhenNoChanges()" persistent max-width="600px">
            <v-card>
                <v-form>
                    <v-card-title>
                        <span class="headline">{{
                            formData.id
                                ? $t('createOrUpdateUser.editTitle')
                                : $t('createOrUpdateUser.createTitle')
                        }}</span>
                    </v-card-title>
                    <v-card-text>
                        <v-container>
                            <v-row>
                                <v-col cols="6">
                                    <v-text-field
                                        v-model="formData.firstName"
                                        :close-on-content-click="true"
                                        :label="
                                            $t(
                                                'createOrUpdateUser.forms.firstName'
                                            )
                                        "
                                        type="string"
                                        :error-messages="firstNameErrors"
                                        autofocus
                                        @input="$v.formData.firstName.$touch()"
                                        @blur="$v.formData.firstName.$touch()"
                                        @keyup="clearServerErrors('firstName')"
                                    />
                                </v-col>
                                <v-col cols="6">
                                    <v-text-field
                                        v-model="formData.lastName"
                                        :label="
                                            $t(
                                                'createOrUpdateUser.forms.lastName'
                                            )
                                        "
                                        type="string"
                                        :error-messages="lastNameErrors"
                                        @input="$v.formData.lastName.$touch()"
                                        @blur="$v.formData.lastName.$touch()"
                                        @keyup="clearServerErrors('lastName')"
                                    />
                                </v-col>
                                <v-col cols="12" sm="6">
                                    <v-menu
                                        v-model="isBirthDatePickerVisible"
                                        :close-on-content-click="false"
                                        transition="scale-transition"
                                        offset-y
                                        min-width="290px"
                                    >
                                        <template
                                            v-slot:activator="{ on, attrs }"
                                        >
                                            <v-text-field
                                                :label="
                                                    $t(
                                                        'createOrUpdateUser.forms.birthDate'
                                                    )
                                                "
                                                prepend-icon="mdi-calendar"
                                                readonly
                                                :value="
                                                    formData.birthDate
                                                        | dateFormatFromSettings
                                                "
                                                :error-messages="
                                                    birthDateErrors
                                                "
                                                v-bind="attrs"
                                                v-on="on"
                                            />
                                        </template>
                                        <v-date-picker
                                            v-model="formData.birthDate"
                                            no-title
                                            scrollable
                                            @input="onBirthDatePickerInput"
                                        />
                                    </v-menu>
                                </v-col>
                                <v-col cols="12" sm="6">
                                    <v-text-field
                                        v-model="formData.phone"
                                        :label="
                                            $t('createOrUpdateUser.forms.phone')
                                        "
                                        type="string"
                                        :error-messages="phoneErrors"
                                        @input="$v.formData.phone.$touch()"
                                        @blur="$v.formData.phone.$touch()"
                                        @keyup="clearServerErrors('phone')"
                                    />
                                </v-col>
                                <v-col cols="12" sm="12">
                                    <v-text-field
                                        v-model="formData.email"
                                        :label="
                                            $t('createOrUpdateUser.forms.email')
                                        "
                                        type="string"
                                        :error-messages="emailErrors"
                                        @input="$v.formData.email.$touch()"
                                        @blur="$v.formData.email.$touch()"
                                        @keyup="clearServerErrors('email')"
                                    />
                                </v-col>
                                <v-col v-if="!user" cols="12" sm="12">
                                    <v-checkbox
                                        v-model="
                                            formData.isPasswordOptionChecked
                                        "
                                        :label="
                                            $t(
                                                'createOrUpdateUser.forms.letUserCreatePassword'
                                            )
                                        "
                                        @click="onPasswordOptionChange"
                                    />
                                </v-col>
                                <v-col
                                    v-if="
                                        !user &&
                                            !formData.isPasswordOptionChecked
                                    "
                                    cols="12"
                                    sm="12"
                                >
                                    <v-text-field
                                        v-model="formData.password"
                                        :label="
                                            $t(
                                                'createOrUpdateUser.forms.password'
                                            )
                                        "
                                        type="password"
                                        :error-messages="passwordErrors"
                                        @input="$v.formData.password.$touch()"
                                        @blur="$v.formData.password.$touch()"
                                        @keyup="clearServerErrors('password')"
                                    />
                                </v-col>
                            </v-row>
                        </v-container>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer />
                        <v-btn color="red darken-1" text @click="onClose">{{
                            $t('createOrUpdateUser.forms.close')
                        }}</v-btn>
                        <v-btn color="blue darken-1" text @click="onSave">{{
                            $t('createOrUpdateUser.forms.save')
                        }}</v-btn>
                    </v-card-actions>
                </v-form>
            </v-card>
        </v-dialog>
    </v-row>
</template>

<script>
import { mapActions } from 'vuex';
import dayjs from 'dayjs';
import createOrEditValidator from '@/validators/users/createOrEditValidator.mixin';

export default {
    name: 'createOrUpdateUser',
    props: {
        isOpen: Boolean,
        user: {
            type: Object,
            default: () => ({})
        }
    },
    mixins: [createOrEditValidator],
    data() {
        return {
            defaultFormData: {
                firstName: '',
                lastName: '',
                birthDate: dayjs().format('YYYY-MM-DD'),
                phone: '',
                email: '',
                password: '',
                isPasswordOptionChecked: true
            },
            formData: {},
            formDataCopy: {},
            isBirthDatePickerVisible: false,
            isLoading: false,
            errorMessage: ''
        };
    },
    watch: {
        user() {
            this.setDefaultFormData();
        }
    },
    created() {
        this.setDefaultFormData();
    },
    methods: {
        ...mapActions({ saveUser: 'users/save' }),

        setDefaultFormData() {
            this.formData = this.user
                ? { ...this.user }
                : { ...this.defaultFormData };

            this.formDataCopy = { ...this.formData }
        },
        setDefaultData() {
            this.setDefaultFormData();
            this.errorMessage = '';
        },
        async onSave() {
            try {
                this.$v.$touch();

                if (!this.isValid) {
                    return;
                }

                await this.saveUser(this.formData);

                this.$notify({
                    group: 'notify',
                    type: 'success',
                    title: this.formData.id
                        ? this.$t('createOrUpdateUser.editTitle')
                        : this.$t('createOrUpdateUser.createTitle'),
                    text: this.$t('createOrUpdateUser.saveSuccess')
                });

                this.onClose();
            } catch (error) {
                this.errorMessage = '';

                if (this.checkForServerFormErrors(error)) {
                    this.$notify({
                        group: 'notify',
                        type: 'error',
                        title: this.formData.id
                            ? this.$t('createOrUpdateUser.editTitle')
                            : this.$t('createOrUpdateUser.createTitle'),
                        text: this.$t('createOrUpdateUser.checkFormErrors')
                    });
                }
            }
        },
        onPasswordOptionChange() {
            if (this.formData.isPasswordOptionChecked) {
                this.formData.password = '';
            }
        },
        async onClose() {
            setTimeout(this.setDefaultData, 100);
            this.$v.$reset();
            this.$emit('close');
        },
        onBirthDatePickerInput() {
            this.$v.formData.birthDate.$touch();
            this.clearServerErrors('birthDate');
            this.isBirthDatePickerVisible = false;
        },
        closeWhenNoChanges() {
            const currentData = Object.entries(this.formData).toString();
            const previousData = Object.entries(this.formDataCopy).toString();

            if (previousData !== currentData) {
                return;
            }

            this.onClose();
        }
    }
};
</script>
